import React, { useState, useEffect } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { PieChart, TrendingUp, TrendingDown, DollarSign, MinusCircle, PlusCircle, Download, RefreshCw } from 'lucide-react';

const DRE = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [dreData, setDreData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  
  const company = JSON.parse(localStorage.getItem('company') || '{}');

  useEffect(() => {
    if (company?.id) {
      fetchDRE();
    }
  }, [company?.id, selectedMonth]);

  const fetchDRE = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/dre/${company.id}?mes=${selectedMonth}`);
      setDreData(response.data);
    } catch (error) {
      console.error('Erro ao carregar DRE:', error);
      toast.error('Erro ao carregar DRE');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  // Gerar lista de meses
  const generateMonths = () => {
    const months = [];
    const currentDate = new Date();
    for (let i = 11; i >= 0; i--) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const value = date.toISOString().slice(0, 7);
      const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
      months.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return months;
  };

  const LineItem = ({ label, value, percentage, isTotal, isPositive, isNegative, indent = 0 }) => (
    <div 
      className={`flex items-center justify-between py-3 px-4 ${
        isTotal ? 'bg-white/5 font-bold border-t border-white/10' : 'border-b border-white/5'
      }`}
      style={{ paddingLeft: `${16 + indent * 16}px` }}
    >
      <span className={`${isTotal ? 'text-white' : 'text-gray-300'}`}>{label}</span>
      <div className="flex items-center gap-4">
        <span className={`font-mono ${
          isPositive ? 'text-emerald-400' : 
          isNegative ? 'text-rose-400' : 
          isTotal ? 'text-white' : 'text-gray-300'
        }`}>
          {formatCurrency(value)}
        </span>
        {percentage !== undefined && (
          <span className="text-xs text-gray-500 w-16 text-right">
            {formatPercent(percentage)}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 p-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <PieChart className="text-purple-400" />
                DRE - Demonstrativo de Resultado
              </h1>
              <p className="text-gray-400 mt-1">Análise de receitas, custos e lucro</p>
            </div>
            <SubscriptionCard user={user} compact={true} />
          </div>

          {/* Filtros */}
          <Card className="glass border-white/10 mb-6">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 max-w-xs">
                  <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Selecione o mês" />
                    </SelectTrigger>
                    <SelectContent>
                      {generateMonths().map((month) => (
                        <SelectItem key={month.value} value={month.value}>
                          {month.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  variant="outline" 
                  onClick={fetchDRE}
                  className="border-zinc-700"
                >
                  <RefreshCw size={16} className="mr-2" />
                  Atualizar
                </Button>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
          ) : dreData ? (
            <>
              {/* Cards de Resumo */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <Card className="glass border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-emerald-500/20">
                        <PlusCircle className="text-emerald-400" size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Receita Bruta</p>
                        <p className="text-lg font-bold text-emerald-400">
                          {formatCurrency(dreData.receita_bruta)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-rose-500/20">
                        <MinusCircle className="text-rose-400" size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Custos Totais</p>
                        <p className="text-lg font-bold text-rose-400">
                          {formatCurrency(dreData.custos_totais)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-500/20">
                        <DollarSign className="text-blue-400" size={20} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Lucro Bruto</p>
                        <p className="text-lg font-bold text-blue-400">
                          {formatCurrency(dreData.lucro_bruto)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="glass border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${dreData.lucro_liquido >= 0 ? 'bg-emerald-500/20' : 'bg-rose-500/20'}`}>
                        {dreData.lucro_liquido >= 0 ? 
                          <TrendingUp className="text-emerald-400" size={20} /> : 
                          <TrendingDown className="text-rose-400" size={20} />
                        }
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Lucro Líquido</p>
                        <p className={`text-lg font-bold ${dreData.lucro_liquido >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {formatCurrency(dreData.lucro_liquido)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* DRE Detalhado */}
              <Card className="glass border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <PieChart size={20} className="text-purple-400" />
                    Demonstrativo de Resultado do Exercício
                    <span className="text-sm font-normal text-gray-400 ml-2">
                      ({new Date(selectedMonth + '-01').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })})
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-white/5">
                    {/* RECEITAS */}
                    <div className="bg-emerald-500/5 px-4 py-2">
                      <span className="text-sm font-semibold text-emerald-400 uppercase tracking-wider">
                        Receitas
                      </span>
                    </div>
                    <LineItem 
                      label="(+) Receita Bruta de Vendas/Serviços" 
                      value={dreData.receita_bruta} 
                      percentage={100}
                      isPositive
                    />
                    <LineItem 
                      label="(-) Deduções (Impostos, Devoluções)" 
                      value={-dreData.deducoes} 
                      percentage={dreData.receita_bruta > 0 ? (dreData.deducoes / dreData.receita_bruta) * 100 : 0}
                      isNegative
                    />
                    <LineItem 
                      label="(=) Receita Líquida" 
                      value={dreData.receita_liquida} 
                      percentage={dreData.receita_bruta > 0 ? (dreData.receita_liquida / dreData.receita_bruta) * 100 : 0}
                      isTotal
                    />

                    {/* CUSTOS */}
                    <div className="bg-rose-500/5 px-4 py-2 mt-2">
                      <span className="text-sm font-semibold text-rose-400 uppercase tracking-wider">
                        Custos e Despesas
                      </span>
                    </div>
                    <LineItem 
                      label="(-) Custo dos Produtos/Serviços Vendidos (CPV)" 
                      value={-dreData.cpv} 
                      percentage={dreData.receita_bruta > 0 ? (dreData.cpv / dreData.receita_bruta) * 100 : 0}
                      isNegative
                    />
                    <LineItem 
                      label="(=) Lucro Bruto" 
                      value={dreData.lucro_bruto} 
                      percentage={dreData.receita_bruta > 0 ? (dreData.lucro_bruto / dreData.receita_bruta) * 100 : 0}
                      isTotal
                    />

                    {/* DESPESAS OPERACIONAIS */}
                    <div className="bg-orange-500/5 px-4 py-2 mt-2">
                      <span className="text-sm font-semibold text-orange-400 uppercase tracking-wider">
                        Despesas Operacionais
                      </span>
                    </div>
                    <LineItem 
                      label="(-) Despesas Administrativas" 
                      value={-dreData.despesas_administrativas} 
                      percentage={dreData.receita_bruta > 0 ? (dreData.despesas_administrativas / dreData.receita_bruta) * 100 : 0}
                      isNegative
                      indent={1}
                    />
                    <LineItem 
                      label="(-) Despesas Comerciais" 
                      value={-dreData.despesas_comerciais} 
                      percentage={dreData.receita_bruta > 0 ? (dreData.despesas_comerciais / dreData.receita_bruta) * 100 : 0}
                      isNegative
                      indent={1}
                    />
                    <LineItem 
                      label="(-) Despesas Financeiras" 
                      value={-dreData.despesas_financeiras} 
                      percentage={dreData.receita_bruta > 0 ? (dreData.despesas_financeiras / dreData.receita_bruta) * 100 : 0}
                      isNegative
                      indent={1}
                    />
                    <LineItem 
                      label="(-) Outras Despesas Operacionais" 
                      value={-dreData.outras_despesas} 
                      percentage={dreData.receita_bruta > 0 ? (dreData.outras_despesas / dreData.receita_bruta) * 100 : 0}
                      isNegative
                      indent={1}
                    />
                    <LineItem 
                      label="(=) Resultado Operacional (EBIT)" 
                      value={dreData.resultado_operacional} 
                      percentage={dreData.receita_bruta > 0 ? (dreData.resultado_operacional / dreData.receita_bruta) * 100 : 0}
                      isTotal
                    />

                    {/* RESULTADO FINAL */}
                    <div className="bg-purple-500/5 px-4 py-2 mt-2">
                      <span className="text-sm font-semibold text-purple-400 uppercase tracking-wider">
                        Resultado Final
                      </span>
                    </div>
                    <LineItem 
                      label="(-) Impostos sobre o Lucro" 
                      value={-dreData.impostos_lucro} 
                      percentage={dreData.receita_bruta > 0 ? (dreData.impostos_lucro / dreData.receita_bruta) * 100 : 0}
                      isNegative
                    />
                    <div className={`flex items-center justify-between py-4 px-4 ${
                      dreData.lucro_liquido >= 0 ? 'bg-emerald-500/10' : 'bg-rose-500/10'
                    } font-bold text-lg`}>
                      <span className="text-white">(=) LUCRO LÍQUIDO DO EXERCÍCIO</span>
                      <div className="flex items-center gap-4">
                        <span className={dreData.lucro_liquido >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                          {formatCurrency(dreData.lucro_liquido)}
                        </span>
                        <span className={`text-sm ${dreData.lucro_liquido >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {formatPercent(dreData.receita_bruta > 0 ? (dreData.lucro_liquido / dreData.receita_bruta) * 100 : 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Margem de Lucro */}
              <Card className="glass border-white/10 mt-6">
                <CardHeader>
                  <CardTitle className="text-white text-lg">Indicadores de Rentabilidade</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white/5 rounded-lg p-4 text-center">
                      <p className="text-xs text-gray-400 mb-1">Margem Bruta</p>
                      <p className="text-2xl font-bold text-blue-400">
                        {formatPercent(dreData.receita_bruta > 0 ? (dreData.lucro_bruto / dreData.receita_bruta) * 100 : 0)}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4 text-center">
                      <p className="text-xs text-gray-400 mb-1">Margem Operacional</p>
                      <p className="text-2xl font-bold text-purple-400">
                        {formatPercent(dreData.receita_bruta > 0 ? (dreData.resultado_operacional / dreData.receita_bruta) * 100 : 0)}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded-lg p-4 text-center">
                      <p className="text-xs text-gray-400 mb-1">Margem Líquida</p>
                      <p className={`text-2xl font-bold ${dreData.lucro_liquido >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {formatPercent(dreData.receita_bruta > 0 ? (dreData.lucro_liquido / dreData.receita_bruta) * 100 : 0)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="glass border-white/10">
              <CardContent className="py-20 text-center">
                <PieChart size={48} className="mx-auto text-gray-600 mb-4" />
                <p className="text-gray-400">Nenhum dado disponível para o período selecionado</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DRE;
